import { useMemo, useState } from 'react'
import { Clock, Pause, Play, Plus, Search, Trash2, History } from 'lucide-react'
import { Page } from '@/components/Page'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { EmptyState } from '@/components/EmptyState'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { Modal } from '@/components/Modal'
import { DataTable } from '@/components/DataTable'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { StatCard } from '@/components/StatCard'
import { useToast } from '@/hooks/useToast'
import { ApiError } from '@/lib/api'
import { useAuth } from '@/auth/useAuth'
import {
  useAllWorkflowRuns,
  useWorkflowAction,
  useWorkflowRuns,
  useWorkflows,
} from '@/features/workflows/queries'
import type { Visibility, Workflow, WorkflowRun } from '@/features/workflows/types'
import { WorkflowFormModal } from './WorkflowFormModal'

type Filter = 'all' | 'mine' | Visibility

const FILTERS: { value: Filter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'mine', label: 'Mine' },
  { value: 'private', label: 'Private' },
  { value: 'tenant', label: 'Team' },
  { value: 'shared', label: 'Shared' },
]

const PLACEHOLDER_DOMAINS = new Set([
  'example.com', 'example.org', 'example.net', 'test.com', 'domain.com', 'yourcompany.com',
])

function cleanRecipients(raw: Workflow['recipients']): string[] {
  const arr = Array.isArray(raw)
    ? raw
    : typeof raw === 'string'
      ? (() => {
          try { return JSON.parse(raw) as unknown } catch { return [] }
        })()
      : []
  if (!Array.isArray(arr)) return []
  return arr
    .filter((e): e is string => typeof e === 'string')
    .filter((e) => {
      const at = e.indexOf('@')
      if (at <= 0) return false
      return !PLACEHOLDER_DOMAINS.has(e.slice(at + 1).toLowerCase())
    })
}

function describeCron(expr?: string | null): string {
  if (!expr) return 'No schedule'
  const parts = expr.trim().split(/\s+/)
  if (parts.length !== 5) return expr
  const [min, hour, dom, mon, dow] = parts
  const hh = (hour ?? '0').padStart(2, '0')
  const mm = (min ?? '0').padStart(2, '0')
  if (dom === '*' && mon === '*' && dow === '*') return `Daily at ${hh}:${mm}`
  if (dom === '*' && mon === '*' && dow !== '*') {
    const map: Record<string, string> = { '0': 'Sun', '1': 'Mon', '2': 'Tue', '3': 'Wed', '4': 'Thu', '5': 'Fri', '6': 'Sat' }
    const day = (dow ?? '').split(',').map((d) => map[d] ?? d).join(', ')
    return `Every ${day} at ${hh}:${mm}`
  }
  if (dom !== '*' && mon === '*' && dow === '*') return `Monthly on day ${dom} at ${hh}:${mm}`
  return expr
}

function fmtDate(iso?: string | null): string {
  if (!iso) return ''
  return new Date(iso).toLocaleString()
}

function fmtDuration(sec?: number | string | null): string {
  if (sec == null || sec === '') return ''
  const n = typeof sec === 'string' ? Number(sec) : sec
  if (!Number.isFinite(n)) return ''
  const s = Math.max(0, Math.round(n))
  if (s < 60) return `${s}s`
  return `${Math.floor(s / 60)}m ${s % 60}s`
}

function describeTrigger(t?: string | null): string {
  if (!t) return '-'
  if (t === 'schedule') return 'Schedule'
  if (t.startsWith('manual:')) return `Manual (user #${t.slice(7)})`
  return t
}

export function WorkflowsPage() {
  const { hasPermission, user } = useAuth()
  const canManage = hasPermission('workflows.manage')
  const canRun = hasPermission('workflows.run')
  const currentUserId = user?.userId ? Number(user.userId) : null

  const { toast } = useToast()
  const q = useWorkflows()
  const action = useWorkflowAction()

  const [tab, setTab] = useState<'workflows' | 'runs'>('workflows')
  const [filter, setFilter] = useState<Filter>('all')
  const [search, setSearch] = useState('')
  const [confirmDelete, setConfirmDelete] = useState<Workflow | null>(null)
  const [runsFor, setRunsFor] = useState<Workflow | null>(null)
  const [detailsRun, setDetailsRun] = useState<WorkflowRun | null>(null)
  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState<Workflow | null>(null)

  const openCreate = () => {
    setEditing(null)
    setFormOpen(true)
  }
  const openEdit = (w: Workflow) => {
    setEditing(w)
    setFormOpen(true)
  }

  const workflows = useMemo(() => q.data?.workflows ?? [], [q.data])

  const stats = useMemo(() => {
    const total = workflows.length
    const active = workflows.filter((w) => w.status === 'active').length
    const mine = currentUserId != null
      ? workflows.filter((w) => Number(w.created_by) === currentUserId).length
      : 0
    const team = workflows.filter((w) => w.visibility === 'tenant').length
    const shared = workflows.filter((w) => w.visibility === 'shared').length
    return { total, active, mine, team, shared }
  }, [workflows, currentUserId])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return workflows.filter((w) => {
      if (filter === 'mine' && Number(w.created_by) !== currentUserId) return false
      if ((filter === 'private' || filter === 'tenant' || filter === 'shared') && w.visibility !== filter) return false
      if (!q) return true
      return (
        (w.name ?? '').toLowerCase().includes(q) ||
        (w.description ?? '').toLowerCase().includes(q)
      )
    })
  }, [workflows, filter, search, currentUserId])

  const runAction = async (w: Workflow, kind: 'run' | 'pause' | 'resume' | 'remove') => {
    try {
      await action.mutateAsync({ id: w.id, kind })
      if (kind === 'run') {
        toast({
          title: 'Triggered',
          description: `Workflow "${w.name}" is running. Check the run history in a few seconds.`,
        })
      }
      if (kind === 'remove') {
        setConfirmDelete(null)
        toast({ title: 'Workflow deleted.' })
      }
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Action failed',
        description: messageFor(err, ''),
      })
    }
  }

  if (q.isLoading) {
    return (
      <Page title="Workflows">
        <Card>
          <CardContent className="py-10">
            <LoadingSpinner />
          </CardContent>
        </Card>
      </Page>
    )
  }

  if (q.isError) {
    return (
      <Page title="Workflows">
        <Alert variant="destructive">
          <AlertDescription>Failed to load workflows: {messageFor(q.error, '')}</AlertDescription>
        </Alert>
      </Page>
    )
  }

  return (
    <Page
      title="Workflows"
      description="Scheduled automations for reporting, notifications, and exports."
      width="wide"
      actions={
        canManage ? (
          <Button onClick={openCreate}>
            <Plus className="size-4" /> New workflow
          </Button>
        ) : undefined
      }
    >
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-5">
        <StatCard label="Total visible" value={stats.total} hint="workflows you can see" />
        <StatCard label="Active" value={stats.active} hint="running on schedule" />
        <StatCard label="Created by you" value={stats.mine} hint="yours to edit" />
        <StatCard label="Team" value={stats.team} hint="shared with everyone" />
        <StatCard label="Shared (specific)" value={stats.shared} hint="picked members only" />
      </div>

      <Tabs value={tab} onValueChange={(v) => setTab(v as 'workflows' | 'runs')}>
        <TabsList>
          <TabsTrigger value="workflows">Workflows</TabsTrigger>
          <TabsTrigger value="runs">Run History</TabsTrigger>
        </TabsList>

        <TabsContent value="workflows" className="mt-4">
      <Card>
        <CardContent className="space-y-4 pt-6">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative min-w-[200px] flex-1">
              <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
              <Input
                value={search}
                onChange={(e) => setSearch(e.currentTarget.value)}
                placeholder="Search by name or description"
                className="pl-9"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {FILTERS.map((f) => (
                <Button
                  key={f.value}
                  variant={filter === f.value ? 'primary' : 'outline'}
                  size="sm"
                  onClick={() => setFilter(f.value)}
                >
                  {f.label}
                </Button>
              ))}
            </div>
          </div>

          {workflows.length === 0 ? (
            <EmptyState
              icon={Clock}
              title="No workflows yet"
              description="Create your first scheduled workflow to automate reporting."
              action={
                canManage ? (
                  <Button onClick={openCreate}>
                    <Plus className="size-4" /> New workflow
                  </Button>
                ) : undefined
              }
            />
          ) : filtered.length === 0 ? (
            <EmptyState
              icon={Search}
              title="No matches"
              description="Try a different search or filter."
            />
          ) : (
            <div className="flex flex-col gap-3">
              {filtered.map((w) => {
                const rec = cleanRecipients(w.recipients)
                const isMine = currentUserId != null && Number(w.created_by) === currentUserId
                const canEditThis = canManage && (isMine || w.visibility === 'tenant')
                return (
                  <div
                    key={w.id}
                    className="rounded-md border bg-card p-4 transition-shadow hover:shadow-card"
                  >
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="flex min-w-0 items-start gap-3">
                        <div className="flex size-10 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                          <Clock className="size-5" aria-hidden />
                        </div>
                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold text-foreground">
                            {w.name}
                          </p>
                          <p className="truncate text-xs text-muted-foreground">
                            {w.description || `${describeCron(w.cron_expression)} · ${w.timezone ?? 'UTC'}`}
                          </p>
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge variant={w.status === 'paused' ? 'warning' : 'success'}>
                          {w.status}
                        </Badge>
                        <Badge variant="secondary">{visibilityLabel(w.visibility)}</Badge>
                      </div>
                    </div>
                    <div className="mt-3 grid gap-3 border-t pt-3 text-xs sm:grid-cols-4">
                      <Meta label="Schedule" value={describeCron(w.cron_expression)} sub={w.timezone ?? 'UTC'} />
                      <Meta label="Recipients" value={rec.length ? rec.join(', ') : '-'} />
                      <Meta
                        label="Last run"
                        value={
                          w.last_started_at ? (
                            <span className="flex items-center gap-1">
                              <Badge variant={w.last_status === 'success' ? 'success' : w.last_status === 'failed' ? 'danger' : 'secondary'}>
                                {w.last_status ?? '-'}
                              </Badge>
                              <span className="text-muted-foreground">{fmtDate(w.last_started_at)}</span>
                            </span>
                          ) : (
                            <span className="text-muted-foreground">Never run</span>
                          )
                        }
                      />
                      <Meta
                        label="Owner"
                        value={
                          <>
                            {w.created_by_name ?? '-'}
                            {isMine ? <span className="text-muted-foreground"> (you)</span> : null}
                          </>
                        }
                      />
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {canRun ? (
                        <Button size="sm" onClick={() => runAction(w, 'run')} disabled={action.isPending}>
                          <Play className="size-4" /> Run now
                        </Button>
                      ) : null}
                      {canEditThis ? (
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => runAction(w, w.status === 'paused' ? 'resume' : 'pause')}
                          disabled={action.isPending}
                        >
                          {w.status === 'paused' ? <Play className="size-4" /> : <Pause className="size-4" />}
                          {w.status === 'paused' ? 'Resume' : 'Pause'}
                        </Button>
                      ) : null}
                      {canEditThis ? (
                        <Button size="sm" variant="secondary" onClick={() => openEdit(w)}>
                          Edit
                        </Button>
                      ) : null}
                      <Button size="sm" variant="ghost" onClick={() => setRunsFor(w)}>
                        <History className="size-4" /> Runs
                      </Button>
                      {canEditThis ? (
                        <Button size="sm" variant="danger" onClick={() => setConfirmDelete(w)}>
                          <Trash2 className="size-4" /> Delete
                        </Button>
                      ) : null}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </CardContent>
      </Card>
        </TabsContent>

        <TabsContent value="runs" className="mt-4">
          <RunHistoryPanel
            enabled={tab === 'runs'}
            onView={(r) => setDetailsRun(r)}
          />
        </TabsContent>
      </Tabs>

      <Modal.Confirm
        open={confirmDelete !== null}
        onOpenChange={(open) => !open && setConfirmDelete(null)}
        title="Delete workflow?"
        body={
          confirmDelete ? (
            <p>
              Delete workflow <strong>{confirmDelete.name}</strong>? This cannot be undone.
            </p>
          ) : null
        }
        onConfirm={() => {
          if (confirmDelete) void runAction(confirmDelete, 'remove')
        }}
        onCancel={() => setConfirmDelete(null)}
        busy={action.isPending}
      />

      <RunsModal
        workflow={runsFor}
        onClose={() => setRunsFor(null)}
        onView={(r) => setDetailsRun(r)}
      />

      <RunDetailsModal run={detailsRun} onClose={() => setDetailsRun(null)} />

      <WorkflowFormModal
        open={formOpen}
        onOpenChange={(open) => {
          setFormOpen(open)
          if (!open) setEditing(null)
        }}
        workflow={editing}
        onSaved={() => {
          toast({ title: editing ? 'Workflow updated.' : 'Workflow created.' })
        }}
      />
    </Page>
  )
}

function Meta({ label, value, sub }: { label: string; value: React.ReactNode; sub?: string }) {
  return (
    <div>
      <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </p>
      <div className="mt-0.5 truncate text-foreground">{value}</div>
      {sub ? <p className="text-[11px] text-muted-foreground">{sub}</p> : null}
    </div>
  )
}

function RunsModal({
  workflow,
  onClose,
  onView,
}: {
  workflow: Workflow | null
  onClose: () => void
  onView: (r: WorkflowRun) => void
}) {
  const runs = useWorkflowRuns(workflow?.id ?? null)
  return (
    <Modal
      open={workflow !== null}
      onOpenChange={(open) => !open && onClose()}
      title={workflow ? `Runs - ${workflow.name}` : 'Runs'}
      className="max-w-3xl"
    >
      {runs.isError ? (
        <Alert variant="destructive">
          <AlertDescription>Failed to load runs.</AlertDescription>
        </Alert>
      ) : (
        <div className="max-h-[60vh] overflow-auto">
          <DataTable
            loading={runs.isLoading}
            rows={runs.data?.runs ?? []}
            empty="No runs yet."
            sortable={false}
            columns={[
              {
                header: 'Started',
                accessorKey: 'started_at',
                cell: ({ row }) => fmtDate(row.original.started_at),
              },
              {
                header: 'Status',
                accessorKey: 'status',
                cell: ({ row }) => (
                  <Badge
                    variant={
                      row.original.status === 'success' ? 'success'
                      : row.original.status === 'failed' ? 'danger'
                      : 'secondary'
                    }
                  >
                    {row.original.status}
                  </Badge>
                ),
              },
              {
                header: 'Duration',
                accessorKey: 'duration_seconds',
                cell: ({ row }) => fmtDuration(row.original.duration_seconds),
              },
              {
                header: 'Triggered by',
                accessorKey: 'triggered_by',
                cell: ({ row }) => (
                  <span className="text-xs text-muted-foreground">
                    {describeTrigger(row.original.triggered_by)}
                  </span>
                ),
              },
              {
                header: '',
                id: 'view',
                cell: ({ row }) => (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => onView(row.original)}
                  >
                    View
                  </Button>
                ),
              },
            ]}
          />
        </div>
      )}
    </Modal>
  )
}

function RunHistoryPanel({
  enabled,
  onView,
}: {
  enabled: boolean
  onView: (r: WorkflowRun) => void
}) {
  const runs = useAllWorkflowRuns(enabled)
  return (
    <Card>
      <CardContent className="pt-6">
        {runs.isError ? (
          <Alert variant="destructive">
            <AlertDescription>Failed to load runs: {messageFor(runs.error, '')}</AlertDescription>
          </Alert>
        ) : (
          <DataTable
            loading={runs.isLoading}
            rows={runs.data?.runs ?? []}
            empty="No runs yet. Trigger a workflow to see its history here."
            sortable={false}
            columns={[
              {
                header: 'Workflow',
                accessorKey: 'workflow_name',
                cell: ({ row }) => (
                  <span className="font-medium">{row.original.workflow_name ?? '-'}</span>
                ),
              },
              {
                header: 'Started',
                accessorKey: 'started_at',
                cell: ({ row }) => fmtDate(row.original.started_at),
              },
              {
                header: 'Duration',
                accessorKey: 'duration_seconds',
                cell: ({ row }) => fmtDuration(row.original.duration_seconds),
              },
              {
                header: 'Status',
                accessorKey: 'status',
                cell: ({ row }) => (
                  <Badge
                    variant={
                      row.original.status === 'success' ? 'success'
                      : row.original.status === 'failed' ? 'danger'
                      : 'secondary'
                    }
                  >
                    {row.original.status}
                  </Badge>
                ),
              },
              {
                header: 'Triggered by',
                accessorKey: 'triggered_by',
                cell: ({ row }) => (
                  <span className="text-xs text-muted-foreground">
                    {describeTrigger(row.original.triggered_by)}
                  </span>
                ),
              },
              {
                header: '',
                id: 'view',
                cell: ({ row }) => (
                  <Button size="sm" variant="ghost" onClick={() => onView(row.original)}>
                    View
                  </Button>
                ),
              },
            ]}
          />
        )}
      </CardContent>
    </Card>
  )
}

type WorkflowRunOutput = {
  workflow_name?: string
  steps?: RunStepResult[]
  reports?: Array<{
    report_type?: string
    period?: { label?: string; start?: string; end?: string } | string | null
    as_of?: string | null
    totals?: Record<string, unknown>
  }>
  duration_ms?: number
}

type RunStepResult = {
  step_order?: number
  step_type?: string
  status?: string
  period?: { label?: string; start?: string; end?: string } | string | null
  subject?: string
  delivered_to?: string[]
  notification_id?: number | string
  [k: string]: unknown
}

const STEP_LABELS: Record<string, string> = {
  generate_income_statement: 'Generate Income Statement',
  generate_balance_sheet: 'Generate Balance Sheet',
  generate_cash_flow: 'Generate Cash Flow',
  generate_trial_balance: 'Generate Trial Balance',
  send_email: 'Send Email',
  create_notification: 'Create Notification',
  // Phase 2b - invoicing action steps
  send_invoice_reminder: 'Send Invoice Reminders',
  generate_recurring_invoice: 'Generate Recurring Invoice',
  mark_invoices_overdue: 'Mark Invoices Overdue',
}

function labelForStep(type?: string): string {
  if (!type) return 'Step'
  return STEP_LABELS[type] ?? type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

function periodLabel(p: RunStepResult['period']): string | null {
  if (!p) return null
  if (typeof p === 'string') return p
  if (p.label) return p.label
  if (p.start && p.end) return `${p.start} → ${p.end}`
  if (p.end) return `As of ${p.end}`
  return null
}

function RunDetailsModal({ run, onClose }: { run: WorkflowRun | null; onClose: () => void }) {
  const hasError = !!run?.error
  const parsed = parseOutput(run?.output)
  return (
    <Modal
      open={run !== null}
      onOpenChange={(open) => !open && onClose()}
      title={run?.workflow_name ? `Run - ${run.workflow_name}` : 'Run details'}
      className="max-w-2xl"
    >
      {run ? (
        <div className="space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-3 rounded-md border bg-muted/30 p-3 text-xs sm:grid-cols-4">
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Status</div>
              <Badge
                variant={
                  run.status === 'success' ? 'success'
                  : run.status === 'failed' ? 'danger'
                  : 'secondary'
                }
              >
                {run.status}
              </Badge>
            </div>
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Started</div>
              <div>{fmtDate(run.started_at)}</div>
            </div>
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Duration</div>
              <div>{fmtDuration(run.duration_seconds) || '-'}</div>
            </div>
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Triggered by</div>
              <div>{describeTrigger(run.triggered_by)}</div>
            </div>
          </div>
          {hasError ? (
            <Alert variant="destructive">
              <AlertDescription>
                <span className="font-semibold">Error:</span> {run.error}
              </AlertDescription>
            </Alert>
          ) : null}
          {parsed && parsed.steps && parsed.steps.length > 0 ? (
            <RunStepsList steps={parsed.steps} />
          ) : parsed && !hasError ? (
            <p className="text-xs text-muted-foreground">No step output recorded.</p>
          ) : null}
          {parsed?.reports && parsed.reports.length > 0 ? (
            <RunReportsList reports={parsed.reports} />
          ) : null}
        </div>
      ) : null}
    </Modal>
  )
}

function parseOutput(output: unknown): WorkflowRunOutput | null {
  if (output == null) return null
  if (typeof output === 'string') {
    try { return JSON.parse(output) as WorkflowRunOutput } catch { return null }
  }
  if (typeof output === 'object') return output as WorkflowRunOutput
  return null
}

function RunStepsList({ steps }: { steps: RunStepResult[] }) {
  const ordered = [...steps].sort(
    (a, b) => (a.step_order ?? 0) - (b.step_order ?? 0),
  )
  return (
    <div className="space-y-2">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        Steps
      </div>
      <ol className="space-y-2">
        {ordered.map((s, idx) => (
          <li
            key={idx}
            className="rounded-md border bg-card p-3 text-xs"
          >
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <span className="flex size-5 items-center justify-center rounded-full bg-primary/10 text-[10px] font-semibold text-primary">
                  {s.step_order ?? idx + 1}
                </span>
                <span className="font-semibold text-foreground">
                  {labelForStep(s.step_type)}
                </span>
              </div>
              <Badge
                variant={
                  s.status === 'ok' || s.status === 'success' ? 'success'
                  : s.status === 'failed' || s.status === 'error' ? 'danger'
                  : 'secondary'
                }
              >
                {s.status ?? 'unknown'}
              </Badge>
            </div>
            <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-muted-foreground">
              {periodLabel(s.period) ? (
                <>
                  <dt className="font-medium">Period</dt>
                  <dd className="text-foreground">{periodLabel(s.period)}</dd>
                </>
              ) : null}
              {s.subject ? (
                <>
                  <dt className="font-medium">Subject</dt>
                  <dd className="text-foreground">{s.subject}</dd>
                </>
              ) : null}
              {Array.isArray(s.delivered_to) && s.delivered_to.length > 0 ? (
                <>
                  <dt className="font-medium">Delivered to</dt>
                  <dd className="text-foreground">{s.delivered_to.join(', ')}</dd>
                </>
              ) : null}
              {s.notification_id != null ? (
                <>
                  <dt className="font-medium">Notification ID</dt>
                  <dd className="text-foreground">{String(s.notification_id)}</dd>
                </>
              ) : null}
            </dl>
          </li>
        ))}
      </ol>
    </div>
  )
}

function RunReportsList({ reports }: { reports: NonNullable<WorkflowRunOutput['reports']> }) {
  return (
    <div className="space-y-2">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        Reports generated
      </div>
      <ul className="space-y-1.5">
        {reports.map((r, i) => {
          const label = labelForStep(r.report_type)
          const period = periodLabel(
            typeof r.period === 'object' ? r.period : (r.period ?? null),
          ) ?? r.as_of ?? null
          return (
            <li
              key={i}
              className="flex items-center justify-between gap-3 rounded-md border bg-muted/30 px-3 py-2 text-xs"
            >
              <span className="font-medium text-foreground">{label}</span>
              {period ? (
                <span className="text-muted-foreground">{period}</span>
              ) : null}
            </li>
          )
        })}
      </ul>
    </div>
  )
}

function visibilityLabel(v: Visibility): string {
  if (v === 'private') return 'Private'
  if (v === 'shared') return 'Shared'
  return 'Team'
}

function messageFor(err: unknown, fallback: string): string {
  if (err instanceof ApiError && err.body && typeof err.body === 'object' && 'error' in err.body) {
    const m = (err.body as { error: unknown }).error
    if (typeof m === 'string' && m) return m
  }
  if (err instanceof Error && err.message) return err.message
  return fallback
}

WorkflowsPage.Meta = Meta
